import { FC, useEffect, useRef } from "react";

import { UseFormReturn } from "react-hook-form";
import { v4 } from "uuid";

import { QuestionField } from "../../../../questions/QuestionField/QuestionField";

import { AdditionalQuestionsForTicket } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/AdditionalQuestionForTicket";
import { AdditionalQuestions } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/AdditionalQuestions";
import { ParticipantAnswers, TicketingPaymentInput } from "@/common/types/ticketing";
import { QuestionOutput } from "@/common/types/trpc";

interface ShopAdditionalQuestionsBlockProps {
  rateId: string;
  rateQuestions: QuestionOutput[];
  shopQuantity: number;
  rateIndex: number;
  form: UseFormReturn<TicketingPaymentInput>;
}

function generateDefaultQuestionAnswer(questionId: string) {
  return {
    id: v4(),
    questionId,
    choiceIds: [],
    answer: "",
    answers: [""],
  };
}

export const ShopAdditionalQuestionsBlock: FC<ShopAdditionalQuestionsBlockProps> = ({
  rateId,
  rateQuestions,
  shopQuantity,
  rateIndex,
  form,
}) => {
  const previousQuantityRef = useRef<number | null>(null);

  // Re-initialize pending answers only when shopQuantity actually changes (tracked via ref),
  // not on every render — otherwise user input would be wiped by rateQuestions reference changes.
  // We merge with existing answers so that raising the quantity from 1 → 2 keeps the answers
  // already provided for the first item.
  // Pending answers are scoped per rateId so two rate blocks visible at once (e.g. hoodie +
  // t-shirt with variants picked) cannot collide on the same form path.
  useEffect(() => {
    if (previousQuantityRef.current === shopQuantity) {
      return;
    }
    previousQuantityRef.current = shopQuantity;

    const existingForRate = (form.getValues(`shopPendingAnswers.${rateId}`) ?? []) as ParticipantAnswers[];

    const pendingAnswers = Array.from({ length: shopQuantity }, (_, idx) => {
      const existingSlot = existingForRate[idx];
      if (existingSlot && Array.isArray(existingSlot.answers) && existingSlot.answers.length > 0) {
        return existingSlot;
      }
      return {
        rateId: "",
        answers: rateQuestions.map((question) => generateDefaultQuestionAnswer(question.id)),
      };
    });
    form.setValue(`shopPendingAnswers.${rateId}`, pendingAnswers as ParticipantAnswers[]);
  }, [shopQuantity, rateQuestions, form, rateId]);

  useEffect(() => {
    return () => {
      const all = form.getValues("shopPendingAnswers") ?? {};
      const { [rateId]: _removed, ...rest } = all;
      form.setValue("shopPendingAnswers", rest);
    };
  }, [form, rateId]);

  if (shopQuantity === 0) {
    return null;
  }

  return (
    <AdditionalQuestions>
      {Array.from({ length: shopQuantity }).map((_, itemIndex) => (
        <AdditionalQuestionsForTicket
          key={itemIndex}
          itemType="item"
          purchasedAmount={shopQuantity}
          groupNumber={itemIndex + 1}
          participantNumber={1}
          isPartOfGroupTicket={false}
        >
          {rateQuestions.map((question, answerIndex) => (
            <QuestionField
              key={question.id}
              name={`shopPendingAnswers.${rateId}.${itemIndex}.answers.${answerIndex}` as never}
              question={question}
              id={`shop-rate-${rateIndex}-item-${itemIndex}-question-${answerIndex}`}
            />
          ))}
        </AdditionalQuestionsForTicket>
      ))}
    </AdditionalQuestions>
  );
};
